import { Box, Button, Stack, Paper, Typography } from "@mui/material";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ImageIcon from "@mui/icons-material/Image";
import React, { useEffect, useRef, useState } from "react";
import { CameraAltRounded } from "@mui/icons-material";

const maxWidth = 500;
export default function CameraCapture({ onCapture }) {
	const videoRef = useRef(null);
	const canvasRef = useRef(null);

	const [stream, setStream] = useState(null);
	const [capturedImg, setCapturedImg] = useState(null);

	useEffect(() => {
		return () => {
			if (stream) {
				stream.getTracks().forEach((t) => t.stop());
			}
		};
	}, [stream]);

	const stopStream = () => {
		if (stream) {
			stream.getTracks().forEach((t) => t.stop());
			setStream(null);
		}
	};

	const openCamera = async () => {
		setCapturedImg(null);
		try {
			const s = await navigator.mediaDevices.getUserMedia({
				video: { facingMode: "environment" },
				audio: false,
			});
			setStream(s);
			videoRef.current.srcObject = s;
			await videoRef.current.play();
		} catch (err) {
			if (err.name === "NotAllowedError") {
				alert("Camera access denied.");
			}

			console.error("Camera Error:", err);
		}
	};

	const capture = () => {
		const video = videoRef.current;
		const canvas = canvasRef.current;
		const ctx = canvas.getContext("2d");

		canvas.width = video.videoWidth;
		canvas.height = video.videoHeight;

		ctx.drawImage(video, 0, 0);

		const dataURL = canvas.toDataURL("image/jpeg", 0.9);
		setCapturedImg(dataURL);

		stopStream();

		fetch(dataURL)
			.then((res) => res.arrayBuffer())
			.then((buf) => {
				const file = new File([buf], "camera.jpg", { type: "image/jpeg" });
				onCapture(file);
			});
	};

	const recapture = () => {
		setCapturedImg(null);
	};

	return (
		<Box
			sx={{
				display: "flex",
				width: "100%",
				alignItems: "flex-start",
				gap: 2,
			}}>
			{/* LEFT SIDE (Video / Image / Placeholder) */}
			<Box>
				{/* Placeholder BEFORE camera opens */}
				{!stream && !capturedImg && (
					<Paper
						elevation={2}
						sx={{
							width: maxWidth,
							maxWidth,
							height: 300,
							display: "flex",
							flexDirection: "column",
							justifyContent: "center",
							alignItems: "center",
							borderRadius: 2,
							color: "#777",
						}}>
						<ImageIcon sx={{ fontSize: 60, mb: 1, color: "#9e9e9e" }} />
						<Typography variant="body1" color="text.secondary">
							No image captured
						</Typography>
					</Paper>
				)}

				{/* LIVE CAMERA */}
				{stream && !capturedImg && (
					<Paper
						elevation={3}
						sx={{
							width: "100%",
							maxWidth,
							borderRadius: 2,
							overflow: "hidden",
						}}>
						<video ref={videoRef} style={{ width: "100%", background: "#000" }} />
					</Paper>
				)}

				{/* DISPLAY CAPTURED IMAGE */}
				{capturedImg && (
					<Paper
						elevation={3}
						sx={{
							width: "100%",
							maxWidth,
							borderRadius: 2,
							overflow: "hidden",
						}}>
						<img src={capturedImg} alt="Captured" style={{ width: "100%" }} />
					</Paper>
				)}
			</Box>

			{/* RIGHT SIDE — BUTTONS */}
			<Stack direction="column" spacing={2} sx={{ width: "140px", justifyContent: "flex-start" }}>
				{/* OPEN CAMERA BUTTON */}
				{
					<Button
						variant="contained"
						startIcon={<PhotoCameraIcon />}
						onClick={openCamera}
						fullWidth
						sx={{ visibility: !stream && !capturedImg ? "visible" : "hidden" }}>
						Open
					</Button>
				}

				{/* CAPTURE BUTTON */}
				{stream && !capturedImg && (
					<Button variant="contained" startIcon={<CameraAltRounded />} onClick={capture} fullWidth>
						Capture
					</Button>
				)}

				{/* RECAPTURE BUTTON */}
				{capturedImg && (
					<Button variant="outlined" color="warning" startIcon={<RestartAltIcon />} onClick={recapture} fullWidth>
						Retake
					</Button>
				)}
			</Stack>

			<canvas ref={canvasRef} style={{ display: "none" }} />
		</Box>
	);
}
